import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { Badge, MISSION_STATUS, MISSION_PHASE_STATES, MISSION_OPEN_STATES, MissionPhaseIcon, fmtDate, fmtPhaseDate } from "../../lib/helpers";
import Card from "../../components/Card";
import HeadStat from "../../components/HeadStat";
import RecordShell from "../../components/RecordShell";
import RdvCard from "../../components/RdvCard";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import AuthFileLink from "../../components/AuthFileLink";
import Conversation, { agencyAuthor } from "../../components/Conversation";
import { ASSISTANT_ID } from "../../components/AssistantAvatar";
import { EmptyState } from "../../components/Feedback";

export default function MissionDetail() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [company, setCompany] = useState(null);
  const fileRef = useRef();
  // Double clic sur un bouton de phase (recette du 15/10, ANO-01) : un seul envoi, bouton grisé pendant l'envoi.
  const [busyPhase, setBusyPhase] = useState(null);
  const phaseBusy = useRef(false);
  // Mission retirée ou confiée à un autre collaborateur : un message et un chemin de retour, plus une page vide
  // (recette du 15/10, ANO-04).
  const load = useCallback(() => api.get(`/collab/missions/${id}`).then((r) => { setM(r.data); setLoadError(null); })
    .catch((e) => setLoadError([403, 404].includes(e?.response?.status) ? "missing" : "failed")), [id]);
  useEffect(() => { load(); api.get(`/collab/missions/${id}/company`).then((r) => setCompany(r.data)).catch(() => {}); }, [id, load]);
  if (!m && loadError) return (
    <EmptyState icon="FolderX" title={loadError === "missing" ? "Mission introuvable" : "Impossible d'afficher cette mission"}
      hint={loadError === "missing" ? "Elle a peut-être été retirée, ou elle ne vous est pas confiée." : "Vérifiez votre connexion, puis rechargez la page."}
      action={<button onClick={() => nav("/missions")} className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover transition-colors">Retour à mes missions</button>} />
  );
  if (!m) return null;

  // Un dépôt refusé (type de fichier, taille) le dit, comme sur la page projet ; jusqu'ici rien ne s'affichait
  // (recette du 15/10, ANO-09).
  const upload = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/collab/missions/${id}/upload`, fd); toast.success("Livrable envoyé"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Envoi impossible, réessayez."); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  // « Tout marquer prêt à valider » (29/09) : les phases en cours / à corriger passent prêtes à valider, les phases à venir restent à Metara.
  const finalize = async () => {
    try { await api.post(`/collab/missions/${id}/finalize`); toast.success("Phases marquées prêtes à valider — Metara est prévenue"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Aucune phase à marquer"); load(); }  // 409 « modifiée ailleurs » : état à jour (30/09)
  };
  // Conversation de la mission (01/10) : pastille de phase facultative ; pastillé, le message attend la réponse de Metara.
  // Consigne d'une phase « à corriger » : le dernier message « Correction demandée » de cette phase dans la conversation
  // (arbitrage n° 3 du 01/10) ; à défaut, l'ancien champ de la phase (consignes d'avant le 01/10).
  const consigneOf = (p) => {
    const last = (m.messages || []).filter((x) => x.kind === "correction" && x.phase_id === p.id).pop();
    return last ? last.text : p.feedback;
  };
  const sendMessage = async (text, phaseId) => { await api.post(`/collab/missions/${id}/message`, { text, phase_id: phaseId }); await load(); };
  const setPhaseState = async (key, status) => {
    if (phaseBusy.current) return;
    phaseBusy.current = true; setBusyPhase(key);
    try { await api.put(`/missions/${id}/phases/${key}/state`, { status }); toast.success("État mis à jour"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action non autorisée"); load(); }
    finally { phaseBusy.current = false; setBusyPhase(null); }
  };

  const progress = (() => { const phs = m.wf_phases || []; return phs.length ? Math.round(phs.filter((p) => p.status === "valide").length / phs.length * 100) : 0; })();

  const header = (
    <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-5">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 mb-1">
            {m.module_label && <span className="text-xs uppercase tracking-[0.2em] font-semibold text-bordeaux">{m.module_label}</span>}
          </div>
          <h1 className="font-display text-2xl text-marine">{m.type}</h1>
          <div className="text-sm text-marine/80 flex items-center gap-3 flex-wrap mt-0.5">
            <span>{m.client_name}</span>
            <span>Rendu {fmtDate(m.deadline)} · {m.days_left} j</span>
            {m.project_name && <span data-testid="collab-mission-project">{m.project_name}</span>}
          </div>
        </div>
        <div className="shrink-0"><Badge status={m.status} map={MISSION_STATUS} /></div>
      </div>
      <div className="grid grid-cols-2 gap-y-3 [&>*:last-child:nth-child(odd)]:col-span-2 mt-4 pt-4 border-t border-marine/10 sm:flex sm:items-stretch sm:gap-1 sm:overflow-x-auto no-scrollbar sm:divide-x sm:divide-marine/10">
        <HeadStat label="Avancement" value={`${progress}%`} />
        <HeadStat label="Phases" value={String((m.wf_phases || []).length)} />
        <HeadStat label="Livrables" value={String((m.deliverables || []).length)} />
      </div>
    </div>
  );

  const renderApercu = () => (
    <div className="space-y-6">
      <Card>
        <h2 className="font-display text-lg text-marine mb-2">Brief</h2>
        <p className="text-sm text-marine leading-relaxed whitespace-pre-line">{m.brief}</p>
      </Card>
      {(() => {
        // Raccourci du geste phase par phase : visible tant qu'une phase est en cours ou à corriger, disparaît ensuite.
        const openCount = (m.wf_phases || []).filter((p) => p.status === "en_cours" || p.status === "a_corriger").length;
        return openCount > 0 && !m.closed && (
          <button onClick={finalize} data-testid="finalize-mission" className="w-full bg-marine text-white rounded-sm py-3 text-sm font-medium hover:bg-marine-hover active:scale-[.99] transition-all flex items-center justify-center gap-2">
            <Icons.CheckCircle2 size={16} /> Tout marquer prêt à valider · {openCount} phase{openCount > 1 ? "s" : ""}
          </button>
        );
      })()}
    </div>
  );

  // Mission terminée par l'agence : lecture seule pour le collaborateur — plus de changement de phase, de dépôt ni de
  // message, refusés aussi par le serveur (recette du 15/10, ANO-19 ; décision de Mélilia, question 2).
  const closedNote = m.closed && (
    <div className="mb-4 rounded-sm border border-bleu/40 bg-bleu-50 px-4 py-3 text-sm text-bleu-fonce flex items-start gap-2" data-testid="mission-closed-note">
      <Icons.CheckCircle2 size={16} className="shrink-0 mt-0.5" />
      <span>Mission terminée : elle reste consultable, mais n'est plus modifiable. Pour la rouvrir, écrivez à Metara.</span>
    </div>
  );

  const renderPhases = () => (
    <div className="bg-sable rounded-sm border border-marine/10 p-5" data-testid="mission-phases-collab">
      {closedNote}
      <div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Phases d'avancement</div>
      <p className="text-xs text-marine/80 mb-3">Une phase validée ouvre la suivante. Sur une phase en cours, marquez votre travail « Prêt à valider » ; validez vous-même celles marquées « À valider ».</p>
      <div className="space-y-2">
        {(() => {
          const phs = m.wf_phases || [];
          return phs.map((p) => {
            // Lot 5 (28/09) : plus de pointeur — une phase « à venir » est verrouillée, une phase ouverte
            // (ni à venir, ni validée) reçoit ses commandes, quelle que soit sa place dans la liste.
            const isOpen = MISSION_OPEN_STATES.includes(p.status);
            const locked = p.status === "a_venir";
            const stMeta = MISSION_PHASE_STATES[p.status] || MISSION_PHASE_STATES.en_cours;
            const canEdit = isOpen && !m.closed;  // mission terminée : lecture seule (question 2)
            const dates = [["Début", p.planned_start], ["Fin prévue", p.planned_end], ["Fin réelle", p.actual_end]].filter(([, v]) => v);
            const late = p.planned_end && p.status !== "valide" && (() => { const s = String(p.planned_end).split("T")[0]; const d = new Date(s.length > 7 ? s : `${s}-01`); const t = new Date(); t.setHours(0, 0, 0, 0); return d < t; })();
            return (
              <div key={p.id} data-testid={`collab-phase-${p.id}`} className={`rounded-sm border p-3 ${isOpen ? "border-marine/50 bg-creme" : locked ? "border-marine/10 carte-a-venir" : "border-marine/10 opacity-80"}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    {locked ? <Icons.Lock size={16} className="text-muet" /> : <MissionPhaseIcon status={p.status} />}
                    <span className="text-sm font-medium text-marine">{p.label}</span>
                    {p.to_validate && p.status !== "valide" && !locked && <span data-testid={`collab-phase-tovalidate-${p.id}`} className="inline-flex items-center gap-1 bg-bleu-50 text-bleu border border-bleu/30 px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wider">À valider</span>}
                    {late && <span data-testid={`collab-phase-late-${p.id}`} className="inline-flex items-center gap-1 bg-rouge-50 text-rouge border border-rouge/40 px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wider">En retard</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[9px] font-medium border ${stMeta.cls}`}>{stMeta.label}</span>
                    {canEdit && (
                      // Le badge dit l'état, les boutons disent l'action (décision du 28/09) : jamais le même mot deux fois.
                      <div className="flex items-center gap-1">
                        {(p.status === "en_cours" || p.status === "a_corriger") && <button onClick={() => setPhaseState(p.id, "pret_a_valider")} disabled={busyPhase === p.id} data-testid={`collab-phase-pret-${p.id}`} className="text-xs rounded-sm px-2 py-1 border bg-marine text-white border-marine hover:bg-marine-hover transition-colors disabled:opacity-50">{p.status === "a_corriger" ? "Correction faite — prêt à valider" : "Marquer prêt à valider"}</button>}
                        {(p.status === "pret_a_valider" || p.status === "bloquee") && <button onClick={() => setPhaseState(p.id, "en_cours")} disabled={busyPhase === p.id} data-testid={`collab-phase-encours-${p.id}`} className="text-xs rounded-sm px-2 py-1 border border-marine/30 text-marine hover:bg-marine/10 transition-colors disabled:opacity-50">Reprendre le travail</button>}
                        {p.to_validate && <button onClick={() => setPhaseState(p.id, "valide")} disabled={busyPhase === p.id} data-testid={`collab-phase-valider-${p.id}`} className="text-xs rounded-sm px-2 py-1 border bg-bordeaux text-white border-bordeaux hover:opacity-90 transition-colors flex items-center gap-1 disabled:opacity-50"><Icons.Check size={14} /> Valider la phase</button>}
                      </div>
                    )}
                  </div>
                </div>
                {p.description && <div className="text-xs text-marine/80 mt-2 whitespace-pre-line" data-testid={`collab-phase-desc-${p.id}`}>{p.description}</div>}
                {dates.length > 0 && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-marine/80">
                    {dates.map(([lbl, v]) => <span key={lbl}><b className="font-medium text-bordeaux">{lbl} :</b> {fmtPhaseDate(v)}</span>)}
                  </div>
                )}
                {p.status === "a_corriger" && consigneOf(p) && (
                  <div className="mt-3 bg-rouge-50 border border-rouge/30 rounded-sm p-2.5" data-testid={`collab-phase-fb-${p.id}`}>
                    <div className="text-xs font-semibold text-rouge mb-0.5">Consignes de correction</div>
                    <div className="text-sm text-marine whitespace-pre-line">{consigneOf(p)}</div>
                  </div>
                )}
                {!locked && <RdvCard rdv={p} testId={p.id} />}
              </div>
            );
          });
        })()}
      </div>
    </div>
  );

  const renderFichiers = () => (
    <div className="space-y-6">
      <Card>
        <h2 className="font-display text-lg text-marine mb-3">Fichiers partagés par Metara</h2>
        {m.shared_files.length === 0 && <p className="text-sm text-marine/80">Aucun fichier partagé.</p>}
        <div className="space-y-2">{m.shared_files.map((f, i) => (
          <div key={i} className="flex items-center justify-between text-sm border border-marine/10 rounded-sm p-2.5">
            <span className="flex items-center gap-2"><Icons.File size={16} className="text-marine" /> {f.name}</span>
            {f.path && <AuthFileLink path={f.path} className="text-marine inline-flex"><Icons.Download size={16} /></AuthFileLink>}
          </div>))}</div>
      </Card>
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg text-marine">Mes livrables</h2>
          {!m.closed && (<>
            <button onClick={() => fileRef.current.click()} data-testid="upload-deliverable" className="border border-marine text-marine rounded-sm px-3 py-1.5 text-sm hover:bg-marine hover:text-white transition-colors flex items-center gap-1"><Icons.Upload size={16} /> Uploader un livrable</button>
            <input ref={fileRef} type="file" className="hidden" onChange={upload} />
          </>)}
        </div>
        {m.deliverables.length === 0 && <p className="text-sm text-marine/80">Aucun livrable envoyé.</p>}
        <div className="space-y-2">{m.deliverables.map((d) => (
          <div key={d.id} className="flex items-center justify-between text-sm border border-marine/10 rounded-sm p-2.5">
            <span className="flex items-center gap-2"><Icons.FileCheck size={16} className="text-bordeaux" /> {d.name}</span>
            <span className="text-xs text-marine/80">Reçu · {fmtDate(d.uploaded_at)}</span>
          </div>))}</div>
      </Card>
    </div>
  );

  const renderConversation = () => {
    const labels = Object.fromEntries((m.wf_phases || []).map((ph) => [ph.id, ph.label]));
    const messages = (m.messages || []).map((x) => ({
      id: x.id, mine: x.sender_role === "collaborator", text: x.text, created_at: x.created_at,
      author: x.sender_role === "admin" ? agencyAuthor(x.sender_name) : x.sender_name,
      assistant: x.sender_id === ASSISTANT_ID,
      badge: x.phase_id ? { kind: "Phase", label: labels[x.phase_id] || "" } : null,
      kindLabel: x.kind === "correction" ? "Correction demandée" : "",
    }));
    return (
      <Conversation messages={messages} selectLabel="Phase concernée" options={(m.wf_phases || []).map((ph) => ({ value: ph.id, label: ph.label }))}
        placeholder="Écrire à Metara…" onSend={m.closed ? null : sendMessage} closedNote="Mission terminée : la conversation est fermée. Pour la rouvrir, écrivez à Metara." highlightId={sp.get("message")} testid="conversation" />
    );
  };

  const renderEntreprise = () => {
    const co = company?.company;
    if (!co) return <Card><p className="text-sm text-marine/80">Aucune information d'entreprise disponible.</p></Card>;
    const bg = co.brand_guide || {};
    return (
      <div className="space-y-6" data-testid="mission-company">
        <Card>
          <div className="flex items-center gap-2 mb-4"><h2 className="font-display text-lg text-marine">{co.name}</h2></div>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
            {co.sector && <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Secteur</div><div className="text-marine">{co.sector}</div></div>}
            {(co.city || co.country) && <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Localisation</div><div className="text-marine">{[co.city, co.country].filter(Boolean).join(", ")}</div></div>}
            {co.website && <div className="sm:col-span-2"><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Site web</div><a href={co.website} target="_blank" rel="noreferrer" className="text-marine hover:underline">{co.website}</a></div>}
          </div>
          {m.module_label && (
            <div className="mt-4"><div className="text-xs uppercase tracking-wider text-marine/80 mb-2">Prestation concernée</div>
              <div className="flex flex-wrap gap-2"><span className="text-xs px-3 py-1 rounded-sm bg-sable border border-marine/30 text-marine">{m.module_label}</span></div></div>
          )}
        </Card>

        <Card>
          <div className="flex items-center gap-2 mb-4"><h2 className="font-display text-lg text-marine">Charte de marque</h2></div>
          {(!bg.slogan && (bg.colors || []).length === 0 && (bg.fonts || []).length === 0) ? (
            <p className="text-sm text-marine/80">Aucun élément de charte renseigné.</p>
          ) : (
            <div className="space-y-4">
              {bg.slogan && <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Slogan</div><div className="text-sm text-marine italic">« {bg.slogan} »</div></div>}
              {(bg.colors || []).length > 0 && (
                <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-2">Couleurs</div>
                  <div className="flex flex-wrap gap-3">{bg.colors.map((c, i) => (<div key={i} className="flex items-center gap-2 text-xs"><span className="w-8 h-8 rounded-sm border border-marine/10" style={{ background: c }} /> {c}</div>))}</div></div>
              )}
              {(bg.fonts || []).length > 0 && (
                <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-2">Typographies</div>
                  <div className="flex flex-wrap gap-2">{bg.fonts.map((f, i) => <span key={i} className="text-sm px-3 py-1 rounded-sm bg-marine-50 text-marine">{f}</span>)}</div></div>
              )}
            </div>
          )}
        </Card>
        {/* Plus de « Documents de l'entreprise » (contrats, CGV) : aucune information contractuelle pour un collaborateur (02/10). */}
      </div>
    );
  };

  const sections = [
    { id: "apercu", label: "Vue d'ensemble", icon: "LayoutDashboard", render: renderApercu },
    { id: "phases", label: "Phases", icon: "ListChecks", render: renderPhases },
    { id: "fichiers", label: "Fichiers", icon: "FolderOpen", render: renderFichiers },
    { id: "entreprise", label: "Entreprise", icon: "Building2", render: renderEntreprise },
    { id: "conversation", label: "Conversation", icon: "MessagesSquare", render: renderConversation },
  ];

  return (
    <div>
      <button onClick={() => nav("/")} className="text-sm text-marine/80 hover:text-marine flex items-center gap-1 mb-3"><Icons.ArrowLeft size={16} className="text-marine" /> Retour</button>
      <RecordShell
        testid="collab-mission-record"
        breadcrumb={[{ label: "Mes missions", to: "/" }, { label: m.type }]}
        header={header}
        sections={sections}
      />
    </div>
  );
}
